import { Novel, ContentDocument } from '@/lib/types';
import {
  DocumentData,
  Firestore,
  Transaction,
  UpdateData,
  deleteField,
  updateDoc,
  setDoc
} from 'firebase/firestore';
import { BaseDao } from './base-dao';

/**
 * Data Access Object for Content documents (Prose).
 * Handles automatic summary mirroring for content entities in 'narrativeMeta.summaries.contents'.
 */
export class ContentDao extends BaseDao {
  constructor(
    firestore: Firestore,
    private readonly synchronizePublication?: (ownerId: string, novelId: string) => Promise<void>
  ) {
    super(firestore);
  }
  /**
   * Fields that are automatically mirrored from the full document to the metadata summary.
   */
  private readonly SUMMARY_FIELDS = [
    'id', 'name', 'preview', 'notes', 'chapterId', 'sceneIds', 'updatedAt', 'wordCount'
  ];

  /**
   * Fetches content by its textId.
   */
  async getContent<T>(
    userId: string,
    novel: Novel,
    contentId: string
  ): Promise<(T & { id: string }) | null> {
    return this.getItem<T>(userId, novel, 'narrative', 'contents', contentId);
  }

  subscribeContent<T>(
    userId: string,
    novel: Novel,
    contentId: string,
    onUpdate: (doc: (T & { id: string }) | null) => void,
    onError: (err: Error) => void
  ): () => void {
    return this.subscribeItem<T>(userId, novel, 'narrative', 'contents', contentId, onUpdate, onError);
  }

  /**
   * Updates a content document and automatically mirrors summary fields.
   */
  async updateContent(
    userId: string,
    novel: Novel,
    contentId: string,
    updates: Partial<ContentDocument>,
    transaction?: Transaction
  ): Promise<void> {
    const docUpdates = { ...updates };
    const now = new Date().toISOString();

    // 1. Update Full Document (always 'split' mode for content)
    const finalDocUpdates = {
      ...docUpdates,
      'audit.updatedAt': now
    };

    // Ensure we use the correct layout for 'contents'
    await this.updateItem(userId, novel, 'narrative', 'contents', contentId, finalDocUpdates as UpdateData<DocumentData>, transaction);

    // 2. Update Summary Mirror
    const summaryUpdates: Record<string, unknown> = {};
    Object.entries(docUpdates).forEach(([key, value]) => {
      if (this.SUMMARY_FIELDS.includes(key)) {
        summaryUpdates[key] = value;
      }
    });

    if (updates.scratchpad !== undefined) {
      summaryUpdates['preview'] = this.generatePreview(updates.scratchpad);
    }

    summaryUpdates['updatedAt'] = now;

    if (Object.keys(summaryUpdates).length > 0) {
      const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', 'contents', contentId, novel.storageLayout);
      if (fieldPath) {
        const payload: UpdateData<DocumentData> = {};
        Object.entries(summaryUpdates).forEach(([key, value]) => {
          payload[`${fieldPath}.${key}`] = value;
        });

        if (transaction) {
          transaction.update(ref, payload);
        } else {
          try {
            await updateDoc(ref, payload);
          } catch (e) {
            try {
              const nested = this.nestPayload(fieldPath, summaryUpdates);
              await setDoc(ref, nested, { merge: true });
            } catch (setErr) {
              console.warn(`Summary update failed for content ${contentId}.`, setErr);
            }
          }
        }
      }
    }
    if (!transaction && this.synchronizePublication) {
      await this.synchronizePublication(userId, novel.id);
    }
  }

  /**
   * Creates a new content document and its summary.
   */
  async createContent(
    userId: string,
    novel: Novel,
    content: ContentDocument,
    transaction?: Transaction
  ): Promise<void> {
    const now = new Date().toISOString();
    const enrichedContent = {
      ...content,
      audit: {
        createdAt: now,
        updatedAt: now
      }
    };

    // 1. Save Full Item
    await this.setItem(userId, novel, 'narrative', 'contents', enrichedContent, transaction);

    // 2. Save Summary Mirror (must include root-level updatedAt)
    const summary: Record<string, unknown> = {
      updatedAt: now,
      preview: this.generatePreview(content.scratchpad)
    };

    Object.entries(enrichedContent).forEach(([key, value]) => {
      if (this.SUMMARY_FIELDS.includes(key) && key !== 'updatedAt' && key !== 'preview') {
        summary[key] = value;
      }
    });

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', 'contents', content.id, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: summary };
      if (transaction) {
        transaction.update(ref, updates);
      } else {
        try {
          await updateDoc(ref, updates);
        } catch (e) {
          try {
            const nested = this.nestPayload(fieldPath, summary);
            await setDoc(ref, nested, { merge: true });
          } catch (setErr) {
            console.warn(`Summary save failed for content ${content.id}.`, setErr);
          }
        }
      }
    }
    if (!transaction && this.synchronizePublication) {
      await this.synchronizePublication(userId, novel.id);
    }
  }

  /**
   * Generates a plain text preview of the prose content.
   */
  private generatePreview(scratchpad: string): string {
    return (scratchpad || '')
      .substring(0, 150)
      .replace(/[#*`]/g, '')
      .replace(/\n/g, ' ')
      .trim();
  }

  /**
   * Deletes a content and its summary.
   */
  async deleteContent(
    userId: string,
    novel: Novel,
    contentId: string,
    transaction?: Transaction
  ): Promise<void> {
    await this.deleteItem(userId, novel, 'narrative', 'contents', contentId, transaction);

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', 'contents', contentId, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: deleteField() };
      if (transaction) {
        transaction.update(ref, updates);
      } else {
        await updateDoc(ref, updates);
      }
    }

    if (!transaction && this.synchronizePublication) {
      await this.synchronizePublication(userId, novel.id);
    }
  }
}
